'use client';

import { useBack } from '@ps/ui/hooks/useBack';
import { cn } from '@ps/ui/utils';
import { ChevronLeft } from 'lucide-react';

/**
 * Floating circular back button pinned to the top-left, staying put while the
 * content scrolls underneath — mirrors RN's `ListingDetailBackButton`.
 *
 *  - `standalone` (full page scrolls on the viewport) → `fixed`, but aligned
 *    to the top-left of the centered `max-w-md` content column rather than the
 *    viewport edge, so it hugs the card instead of floating in the gutter on
 *    wide screens.
 *  - `drawer` (scrolls inside the panel) → `absolute` to the panel; rendered
 *    as a sibling of the scroll container so it doesn't move with the content.
 *
 * Back behavior goes through {@link useBack}: it pops the in-app history when
 * there's a page to return to (the intercepted drawer, or a soft-nav from the
 * bio), else REPLACES with `fallbackHref` — the creator's bio home — so a
 * cold load / shared permalink / SEO hit (no in-app history) still lands
 * somewhere in-store instead of doing nothing. When `onBack` is supplied (the
 * `/my-popstore` state-driven aside, which has no route to pop), it wins.
 */
export function BackButton({
  variant = 'standalone',
  onBack,
  fallbackHref,
}: {
  variant?: 'standalone' | 'drawer';
  onBack?: () => void;
  /** Where "back" lands when there's no in-app history — the creator's bio home. */
  fallbackHref?: string;
}) {
  const { goBack } = useBack(fallbackHref);
  const button = (
    <button
      type="button"
      aria-label="Go back"
      onClick={onBack ?? goBack}
      className={cn(
        // High z so it stays above any top banner / sticky header / nav.
        'absolute left-4 top-4 z-[60] flex size-9 items-center justify-center rounded-full bg-white text-black shadow-sm transition hover:bg-zinc-50',
      )}
    >
      <ChevronLeft className="size-5" aria-hidden />
    </button>
  );

  if (variant === 'drawer') return button;

  // Standalone: a fixed, full-width row whose inner column matches the PDP
  // content width (`max-w-[560px]` — see ProductDetail), so `left-4` lands at
  // the card's left edge, not the viewport's nor a narrower column's.
  // `pointer-events-none` lets clicks through everywhere except the button.
  //
  // `z-40`: above the PDP content + sticky header (`z-30`), but BELOW any
  // full-screen overlay that covers the page — the checkout / download-success
  // intercept renders at `z-50` (`@checkout` `ModalShell`), and the fullscreen
  // image viewer at `z-70`. At the old `z-[60]` this fixed button punched
  // through the checkout backdrop and floated over the success card. (The
  // inner button keeps `z-[60]`, but that resolves WITHIN this wrapper's
  // stacking context, so it can't escape above the overlay.)
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-40">
      <div className="relative mx-auto max-w-[560px] [&>button]:pointer-events-auto">
        {button}
      </div>
    </div>
  );
}
